import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useData } from '@hooks/useData';
import { formatDate, formatPhoneNumber, formatPrice, formatTime } from '@utils/format';
import { Table } from '@components/atoms/Table';
import { Card } from '@components/atoms/Card';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { SITE_URLS } from '@constants';
import { useGetMyStoresProductReservationDetail } from '@services/useGetMyStoresProductReservationDetail';
import styles from './ManageProductReservationDetail.module.css';
import { ManagementCard } from '@components/molecules/ManagementCard';

export const ManageProductReservationDetail: FC = () => {
    const { uuid } = useParams();
    const { myStoresProductReservationDetail, isLoadingMyStoresProductReservationDetail } = useData();
    const { request: requestMyStoresProductReservationDetail } = useGetMyStoresProductReservationDetail();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('manage-product-reservation-detail-component.title'),
        clientLabel: t('manage-product-reservation-detail-component.client.label'),
        nameLabel: t('manage-product-reservation-detail-component.name.label'),
        emailLabel: t('manage-product-reservation-detail-component.email.label'),
        phoneLabel: t('manage-product-reservation-detail-component.phone.label'),
        messageLabel: t('manage-product-reservation-detail-component.message.label'),
        dateLabel: t('manage-product-reservation-detail-component.date.label'),
        merchantLabel: t('manage-product-reservation-detail-component.merchant.label'),
        productLabel: t('manage-product-reservation-detail-component.product.label'),
        quantityLabel: t('manage-product-reservation-detail-component.quantity.label'),
        totalLabel: t('manage-product-reservation-detail-component.total.label'),
    };

    // fetch order detail info
    useEffect(() => {
        requestMyStoresProductReservationDetail({ uuid: uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    return (
        <MyAccountContainer
            title={translations.title}
            overrideOnDesktop={
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <Typography classes="mb-2" color="muted" fontSize="16">
                        <>{translations.title}</>
                    </Typography>
                </div>
            }>
            <>
                {!isLoadingMyStoresProductReservationDetail && (
                    <>
                        {/* date */}
                        <div className="mb-3">
                            <Typography classes="d-inline-block pe-2" fontSize="16" color="muted">
                                <>{translations.dateLabel}: </>
                            </Typography>
                            <>
                                {formatDate(myStoresProductReservationDetail?.data?.created_at || null)}
                                <Typography tagVariant={TypographyTagVariants.span} classes="text-muted ms-2">
                                    <>
                                        {myStoresProductReservationDetail?.data?.created_at &&
                                            formatTime(myStoresProductReservationDetail.data.created_at)}
                                    </>
                                </Typography>
                            </>
                        </div>
                        {/* client data */}
                        <ManagementCard
                            title={translations.clientLabel}
                            content={[
                                {
                                    label: translations.nameLabel,
                                    content: myStoresProductReservationDetail?.data?.client?.name || '',
                                },
                                {
                                    label: translations.emailLabel,
                                    content: myStoresProductReservationDetail?.data?.client?.email || '',
                                },
                                {
                                    label: translations.phoneLabel,
                                    content: formatPhoneNumber(myStoresProductReservationDetail?.data?.client?.phone || ''),
                                },
                                {
                                    label: translations.messageLabel,
                                    content: myStoresProductReservationDetail?.data?.message || '',
                                },
                            ]}
                        />
                        <div className="row g-4 mt-1 mb-2">
                            <div className="col-12">
                                <Card>
                                    <Typography classes="px-4 pt-4 mx-1" fontSize="16">
                                        <>
                                            {`${translations.merchantLabel} `}
                                            <Anchor
                                                href={SITE_URLS.merchantDetail.replace(
                                                    ':uuid',
                                                    `${myStoresProductReservationDetail?.data?.merchant?.uuid}`
                                                )}
                                                mode={AnchorMode.none}>
                                                <Typography classes="d-inline-block" color="primary" fontWeight="800">
                                                    {myStoresProductReservationDetail?.data?.merchant?.name || ''}
                                                </Typography>
                                            </Anchor>
                                        </>
                                    </Typography>
                                    <Table
                                        borderless={true}
                                        hideHeaderOnMobile={true}
                                        headerCols={[
                                            { classes: 'text-start', text: translations.productLabel },
                                            { classes: 'text-center', text: translations.quantityLabel, fit: true },
                                            { classes: 'text-end', text: translations.totalLabel },
                                        ]}
                                        contentRows={[
                                            [
                                                {
                                                    content: (
                                                        <div className="d-flex gap-3 align-items-start text-decoration-none my-3">
                                                            <div className="mt-2">
                                                                {myStoresProductReservationDetail?.data?.product_image && (
                                                                    <img
                                                                        className={styles['product-image']}
                                                                        src={myStoresProductReservationDetail.data.product_image}
                                                                        alt={myStoresProductReservationDetail.data.product_name}
                                                                    />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <Anchor
                                                                    href={SITE_URLS.productDetail.replace(
                                                                        ':uuid',
                                                                        `${myStoresProductReservationDetail?.data?.product_uuid}`
                                                                    )}
                                                                    mode={AnchorMode.none}>
                                                                    <Typography
                                                                        classes="mt-2 mb-1 pt-1"
                                                                        color="dark"
                                                                        fontSize="14"
                                                                        fontWeight="900"
                                                                        breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                                        {myStoresProductReservationDetail?.data?.product_name}
                                                                    </Typography>
                                                                </Anchor>
                                                                {(
                                                                    myStoresProductReservationDetail?.data?.product_attributes ||
                                                                    []
                                                                ).map((attribute, index) => (
                                                                    <Typography
                                                                        key={`attribute-${index}`}
                                                                        classes="mb-1"
                                                                        color="dark"
                                                                        fontSize="14"
                                                                        fontWeight="400"
                                                                        breakpointsStyles={{ md: { fontSize: '16' } }}>
                                                                        {attribute}
                                                                    </Typography>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ),
                                                },
                                                {
                                                    content: (
                                                        <Typography fontSize="16" align="center">
                                                            {`${myStoresProductReservationDetail?.data?.quantity}`}
                                                        </Typography>
                                                    ),
                                                },
                                                {
                                                    content: (
                                                        <Typography fontSize="16" align="right">
                                                            {formatPrice(myStoresProductReservationDetail?.data?.total)}
                                                        </Typography>
                                                    ),
                                                },
                                            ],
                                        ]}
                                    />
                                </Card>
                            </div>
                        </div>
                    </>
                )}
            </>
        </MyAccountContainer>
    );
};
